/**
 * /producao/disposicao — Disposição de peças (Lote D — auditoria §2.7).
 *
 * Fila do PCP com as peças REPROVADAS na Conferência que ficaram retidas
 * aguardando decisão. Três saídas por peça (gate ordem_producao:reposicao):
 *
 *   - Repor: cancela a retida e cria peça-filha (reusa registrar_reposicao
 *     — categoria 6M obrigatória + observação; filha fica fora do
 *     throughput, D3).
 *   - Retrabalhar: a peça volta ao chão e bipa a Conferência de novo.
 *   - Aceitar: aceite COM CONCESSÃO — segue adiante mesmo com a NC
 *     registrada (observação obrigatória: quem aceita assina o porquê).
 *
 * Leitura da fila é `ppcp:view` (mesmo gate da entrada na sidebar, cujo
 * badge conta esta fila). Datas em dd/mmm hh:mm (F.2); tabela
 * table-layout:fixed com ellipsis (zero scroll lateral).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import EmptyState from "../../components/EmptyState";
import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { notificarDisposicaoMudou } from "../../hooks/useDisposicaoPendentes";
import SlideOverPanel from "../../components/SlideOverPanel";
import {
  CATEGORIAS_6M,
  DECISAO_DISPOSICAO_LABELS,
  decidirDisposicaoPeca,
  filaDisposicao,
} from "../../services/producaoV2";
import { formatarDataHoraCurta } from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import {
  Card,
  ErrorMsg,
  InfoMsg,
  Pill,
  SecondaryButton,
  Select,
  WarningMsg,
} from "../Orcamentos/styles";
import { LinhaErro, Mono, NotaDiscreta, Shimmer, TabelaFixa } from "./_shared/ui";
import { LABEL_CATEGORIA } from "./categorias6m";

/** Label 6M amigável; código desconhecido volta como veio (honesto). */
function rotuloCategoria6m(codigo) {
  return (codigo && (LABEL_CATEGORIA[codigo] || codigo)) || "NC";
}

const CampoForm = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  marginBottom: 16,
};

const RotuloCampo = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--ink-2)",
};

const areaTextoStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--ink)",
  fontSize: "0.88rem",
  fontFamily: "inherit",
  resize: "vertical",
};

/** Copy por decisão — o modal explica a consequência antes do clique. */
const INFO_DECISAO = {
  repor: {
    titulo: "Repor peça",
    aviso:
      "A peça retida é CANCELADA e uma peça-filha nova é criada no mesmo "
      + "lote/ambiente (fora do throughput). A categoria 6M alimenta o "
      + "Ishikawa.",
    observacaoObrigatoria: true,
    placeholderObs: "O que aconteceu com a peça? (vira a descrição da reposição)",
  },
  retrabalhar: {
    titulo: "Retrabalhar peça",
    aviso:
      "A peça volta ao chão de fábrica e precisa bipar a Conferência de "
      + "novo — a retenção é liberada, o histórico da NC permanece.",
    observacaoObrigatoria: false,
    placeholderObs: "Instrução do retrabalho (opcional)",
  },
  aceitar: {
    titulo: "Aceitar peça (com concessão)",
    aviso:
      "Aceite com concessão: a peça segue adiante MESMO com a "
      + "não-conformidade registrada. A observação é obrigatória — quem "
      + "aceita assina o porquê.",
    observacaoObrigatoria: true,
    placeholderObs: "Por que a peça pode seguir mesmo com a NC? *",
  },
};

// ---------------------------------------------------------------------------
// Modal de decisão
// ---------------------------------------------------------------------------

function DecisaoPanel({ alvo, onFechar, onDecidida }) {
  const { request } = useAuth();
  const [categoria, setCategoria] = useState("");
  const [observacao, setObservacao] = useState("");

  const aberta = Boolean(alvo);
  const peca = alvo?.peca;
  const decisao = alvo?.decisao;
  const info = INFO_DECISAO[decisao] || null;

  useEffect(() => {
    if (aberta) {
      setCategoria("");
      setObservacao("");
    }
  }, [aberta, decisao, peca?.id]);

  const exigeCategoria = decisao === "repor";
  const valido =
    Boolean(decisao)
    && (!exigeCategoria || Boolean(categoria))
    && (!info?.observacaoObrigatoria || Boolean(observacao.trim()));

  return (
    <SlideOverPanel
      open={aberta}
      onClose={onFechar}
      title={info ? `${info.titulo} ${peca?.numero || ""}` : "Disposição"}
      subtitle={peca?.descricao}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
          <SecondaryButton type="button" onClick={onFechar}>
            Voltar
          </SecondaryButton>
          {/* repor exige MFA recente (step-up global cobre o 403). */}
          <AsyncButton
            variant={decisao === "repor" ? "danger" : "primary"}
            disabled={!valido}
            silentError
            successMessage={
              decisao
                ? `Disposição registrada: ${DECISAO_DISPOSICAO_LABELS[decisao]}.`
                : "Disposição registrada."
            }
            onClick={async () => {
              await decidirDisposicaoPeca(request, peca.id, {
                decisao,
                observacao: observacao.trim(),
                categoria: exigeCategoria ? categoria : undefined,
              });
              notificarDisposicaoMudou();
              onDecidida();
              onFechar();
            }}
          >
            {info?.titulo || "Confirmar"}
          </AsyncButton>
        </div>
      }
    >
      {info && decisao === "aceitar" ? (
        <WarningMsg style={{ marginBottom: 16 }}>{info.aviso}</WarningMsg>
      ) : info ? (
        <InfoMsg style={{ marginBottom: 16 }}>{info.aviso}</InfoMsg>
      ) : null}

      {peca && (
        <NotaDiscreta style={{ margin: "0 0 16px" }}>
          {peca.ambiente
            ? `Ambiente ${peca.ambiente.numero} · ${peca.ambiente.nome} — `
            : ""}
          pedido {peca.pedido_numero || "–"}
          {peca.cliente_nome ? ` · ${peca.cliente_nome}` : ""}
        </NotaDiscreta>
      )}

      {exigeCategoria && (
        <label style={CampoForm}>
          <span style={RotuloCampo}>Categoria da causa (6M) *</span>
          <Select value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Selecione…</option>
            {CATEGORIAS_6M.map((c) => (
              <option key={c.codigo} value={c.codigo}>
                {c.label}
              </option>
            ))}
          </Select>
        </label>
      )}

      <label style={CampoForm}>
        <span style={RotuloCampo}>
          Observação {info?.observacaoObrigatoria ? "*" : "(opcional)"}
        </span>
        <textarea
          rows={3}
          maxLength={300}
          style={areaTextoStyle}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder={info?.placeholderObs || ""}
        />
      </label>
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function Disposicao() {
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();

  const podeVer = hasPermission("ppcp:view");
  const podeDecidir = hasPermission("ordem_producao:reposicao");

  const [pecas, setPecas] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [alvo, setAlvo] = useState(null); // {peca, decisao}

  const carregar = useCallback(async () => {
    if (!podeVer) {
      setCarregando(false);
      return;
    }
    setErro(null);
    setCarregando(true);
    try {
      const payload = await filaDisposicao(request);
      setPecas(Array.isArray(payload?.pecas) ? payload.pecas : []);
    } catch (err) {
      setErro(
        err?.payload?.detalhe || err?.message
        || "Não foi possível carregar a fila de disposição.",
      );
    } finally {
      setCarregando(false);
    }
  }, [request, podeVer]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (!podeVer) {
    return (
      <PageContainer>
        <EntityHeader
          title="Disposição de peças"
          subtitle="Peças reprovadas na Conferência aguardando decisão do PCP"
        />
        <EmptyState
          tone="locked"
          titulo="Sem acesso à fila de disposição"
          descricao="Esta tela exige a permissão ppcp:view. Fale com o gestor de acessos."
        />
      </PageContainer>
    );
  }

  const total = (pecas || []).length;

  return (
    <PageContainer>
      <EntityHeader
        title="Disposição de peças"
        subtitle="Peças reprovadas na Conferência aguardando decisão do PCP"
        badges={
          total > 0
            ? [{ key: "fila", label: `${total} aguardando`, tone: "danger" }]
            : []
        }
      />

      {erro && (
        <LinhaErro style={{ marginBottom: 12 }}>
          <ErrorMsg style={{ flex: 1 }}>{erro}</ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {carregando ? (
        <Shimmer $h={220} />
      ) : !erro && total === 0 ? (
        <EmptyState
          tone="ok"
          titulo="Nenhuma peça aguardando disposição"
          descricao="Peças reprovadas na Conferência ficam retidas aqui até o PCP decidir repor, retrabalhar ou aceitar."
        />
      ) : !erro ? (
        <Card>
          <TabelaFixa>
            <colgroup>
              <col style={{ width: "12%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "19%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Peça</th>
                <th>Ambiente</th>
                <th>Pedido · Cliente</th>
                <th>Refugo</th>
                <th>NCs</th>
                <th>Retida desde</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pecas.map((p) => {
                // Backend real entrega `nao_conformidades`/`retida_em`;
                // `ncs`/`retida_desde` era o contrato combinado (fallback).
                const ncs = Array.isArray(p.nao_conformidades)
                  ? p.nao_conformidades
                  : Array.isArray(p.ncs)
                    ? p.ncs
                    : [];
                const tituloNcs = ncs
                  .map((nc) =>
                    `${rotuloCategoria6m(nc.categoria || nc.tipo)}${nc.descricao ? `: ${nc.descricao}` : ""}`)
                  .join(" · ");
                return (
                  <tr key={p.id}>
                    <td title={p.descricao || p.numero}>
                      <Mono>{p.numero}</Mono>
                    </td>
                    <td
                      title={
                        p.ambiente
                          ? `${p.ambiente.numero} · ${p.ambiente.nome} — abrir ambiente`
                          : ""
                      }
                      onClick={() =>
                        p.ambiente?.id
                        && navigate(`/producao/ambientes/${p.ambiente.id}`)
                      }
                      style={p.ambiente?.id ? { cursor: "pointer" } : undefined}
                    >
                      {p.ambiente
                        ? `${p.ambiente.numero} · ${p.ambiente.nome}`
                        : "–"}
                    </td>
                    <td
                      title={`${p.pedido_numero || "–"}${p.cliente_nome ? ` · ${p.cliente_nome}` : ""}`}
                    >
                      {p.pedido_numero || "–"}
                      {p.cliente_nome ? ` · ${p.cliente_nome}` : ""}
                    </td>
                    <td>
                      <Mono>{Number(p.qtd_refugo) > 0 ? p.qtd_refugo : "–"}</Mono>
                    </td>
                    <td title={tituloNcs} style={{ overflow: "hidden" }}>
                      {ncs.length === 0
                        ? "–"
                        : ncs.map((nc, idx) => (
                            <Pill
                              key={idx}
                              $tone="danger"
                              title={nc.descricao || rotuloCategoria6m(nc.categoria || nc.tipo)}
                              style={idx > 0 ? { marginLeft: 4 } : undefined}
                            >
                              {rotuloCategoria6m(nc.categoria || nc.tipo)}
                            </Pill>
                          ))}
                    </td>
                    <td>
                      <Mono>{formatarDataHoraCurta(p.retida_em || p.retida_desde)}</Mono>
                    </td>
                    <td style={{ overflow: "visible" }}>
                      {podeDecidir ? (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <SecondaryButton
                            type="button"
                            style={{ padding: "3px 10px", fontSize: "0.75rem" }}
                            title="Cancela a retida e cria peça-filha nova (fora do throughput)"
                            onClick={() => setAlvo({ peca: p, decisao: "repor" })}
                          >
                            Repor
                          </SecondaryButton>
                          <SecondaryButton
                            type="button"
                            style={{ padding: "3px 10px", fontSize: "0.75rem" }}
                            title="A peça volta a bipar a Conferência"
                            onClick={() =>
                              setAlvo({ peca: p, decisao: "retrabalhar" })
                            }
                          >
                            Retrabalhar
                          </SecondaryButton>
                          <SecondaryButton
                            type="button"
                            style={{ padding: "3px 10px", fontSize: "0.75rem" }}
                            title="Aceite com concessão — observação obrigatória"
                            onClick={() => setAlvo({ peca: p, decisao: "aceitar" })}
                          >
                            Aceitar
                          </SecondaryButton>
                        </span>
                      ) : (
                        <Pill
                          $tone="neutral"
                          title="Decidir exige a permissão ordem_producao:reposicao"
                        >
                          somente leitura
                        </Pill>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TabelaFixa>
          {total > 0 && (
            <NotaDiscreta>
              Enquanto retida, a peça não aceita bipagem — a decisão aqui
              libera o fluxo (repor/retrabalhar) ou encerra com concessão.
            </NotaDiscreta>
          )}
        </Card>
      ) : null}

      <DecisaoPanel
        alvo={alvo}
        onFechar={() => setAlvo(null)}
        onDecidida={carregar}
      />
    </PageContainer>
  );
}
